import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { Button, Input, InputOTP, InputOTPGroup, InputOTPSlot, Label } from '@jq4race/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAuthServices } from '../auth-services-context';
import { useAuthState } from '../auth-state-context';
import { AuthShell } from '../components/AuthShell';
import { EmailOtpForm } from '../components/EmailOtpForm';
import { ErrorBanner } from '../components/ErrorBanner';

const OTP_LENGTH = 6;
const OTP_SLOT_IDS = Array.from({ length: OTP_LENGTH }, (_, i) => `otp-slot-${i}`);

type Mode = 'totp' | 'email' | 'recovery';

function buildCodeSchema(t: (key: string) => string) {
  return z.object({
    code: z.string().regex(/^\d{6}$/, t('mfa.codeValidation')),
  });
}

/**
 * Recovery codes must be validated against the same contract as the backend's
 * VerifyMfaRequestSchema (packages/shared/src/auth-schemas.ts): a plain
 * `z.string().min(6).max(20)`. Do NOT tighten this to the current 10-char
 * base64url shape (e.g. `/^[A-Za-z0-9_-]{10}$/`) — a client-side check
 * stricter than the server contract is exactly the class of bug this form
 * used to have (a 6-digit-only regex rejecting valid recovery codes).
 */
function buildRecoveryCodeSchema(t: (key: string) => string) {
  return z.object({
    code: z
      .string()
      .trim()
      .min(6, t('mfa.recoveryCodeValidation'))
      .max(20, t('mfa.recoveryCodeValidation')),
  });
}

type CodeFormValues = z.infer<ReturnType<typeof buildCodeSchema>>;

export function LoginMfaPage() {
  const { t } = useTranslation('auth');
  const auth = useAuthServices();
  const { getMfaIntent, setSession } = useAuthState();
  const [intent] = useState(() => getMfaIntent());
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [mode, setMode] = useState<Mode>('totp');

  const codeSchema = useMemo(
    () => (mode === 'recovery' ? buildRecoveryCodeSchema(t) : buildCodeSchema(t)),
    [t, mode],
  );
  const form = useForm<CodeFormValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  });

  /**
   * Resetting the form also clears react-hook-form's `isSubmitting` (there is
   * no `keepIsSubmitting` option), which would re-enable the submit button
   * while a verify request is still in flight and allow a second, overlapping
   * submission. The mode-switch buttons are disabled while submitting so that
   * cannot happen.
   */
  function switchMode(next: Mode) {
    setMode(next);
    form.reset({ code: '' });
    setError(null);
  }

  async function onSubmit(values: CodeFormValues) {
    if (!intent) return;
    setError(null);
    try {
      const session = await auth.verifyMfa({
        mfaToken: intent.mfaToken,
        code: values.code,
        email: intent.email,
      });
      setSession(session);
      await navigate({ to: '/dashboard' });
    } catch (err) {
      setError(err);
    }
  }

  const { errors, isSubmitting } = form.formState;

  const description: Record<Mode, string> = {
    totp: t('mfa.description'),
    email: t('emailOtp.description'),
    recovery: t('mfa.recoveryDescription'),
  };

  return (
    <AuthShell
      title={t('mfa.title')}
      eyebrow={t('mfa.eyebrow')}
      description={description[mode]}
      aside={{
        illustration: 'cipher',
        eyebrow: t('mfa.aside.eyebrow'),
        headline: t('mfa.aside.headline'),
        body: t('mfa.aside.body'),
      }}
      footer={
        <Link to="/login" className="font-medium text-jq-navy underline underline-offset-2">
          {t('shared.backToSignIn')}
        </Link>
      }
    >
      {mode === 'email' && intent ? (
        <EmailOtpForm
          mfaToken={intent.mfaToken}
          email={intent.email}
          onVerified={async (session) => {
            setSession(session);
            await navigate({ to: '/dashboard' });
          }}
          onBack={() => switchMode('totp')}
          backLabel={t('mfa.backToAuthenticator')}
        />
      ) : (
        <form className="grid gap-5" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <ErrorBanner error={error} />
          {mode === 'recovery' ? (
            <div className="grid gap-3">
              <Label htmlFor="code">{t('mfa.recoveryCodeLabel')}</Label>
              <Controller
                control={form.control}
                name="code"
                render={({ field }) => (
                  <Input
                    id="code"
                    type="text"
                    autoComplete="one-time-code"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.code ? (
                <p className="text-[13px] text-jq-danger">{errors.code.message}</p>
              ) : (
                <p className="text-[12px] text-jq-graphite">{t('mfa.recoveryCodeTip')}</p>
              )}
            </div>
          ) : (
            <div className="grid gap-3">
              <Label htmlFor="code">{t('mfa.codeLabel')}</Label>
              <Controller
                control={form.control}
                name="code"
                render={({ field }) => (
                  <InputOTP
                    id="code"
                    maxLength={OTP_LENGTH}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="^[0-9]+$"
                    value={field.value}
                    onChange={field.onChange}
                    onComplete={form.handleSubmit(onSubmit)}
                    containerClassName="w-full"
                  >
                    <InputOTPGroup className="w-full gap-2 sm:gap-3">
                      {OTP_SLOT_IDS.map((id, i) => (
                        <InputOTPSlot
                          key={id}
                          index={i}
                          className="h-14 flex-1 text-[22px] sm:h-16"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                )}
              />
              {errors.code ? (
                <p className="text-[13px] text-jq-danger">{errors.code.message}</p>
              ) : (
                <p className="text-[12px] text-jq-graphite">{t('mfa.codeTip')}</p>
              )}
            </div>
          )}
          <Button type="submit" disabled={isSubmitting} className="w-full">
            {isSubmitting ? t('mfa.submitting') : t('mfa.submit')}
          </Button>
          {mode === 'recovery' ? (
            <>
              <Button
                type="button"
                variant="link"
                className="justify-self-start px-0"
                disabled={isSubmitting}
                onClick={() => switchMode('totp')}
              >
                {t('mfa.backToAuthenticator')}
              </Button>
              <Button
                type="button"
                variant="link"
                className="justify-self-start px-0"
                disabled={isSubmitting}
                onClick={() => switchMode('email')}
              >
                {t('mfa.useEmailInstead')}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="link"
                className="justify-self-start px-0"
                disabled={isSubmitting}
                onClick={() => switchMode('email')}
              >
                {t('mfa.useEmailInstead')}
              </Button>
              <Button
                type="button"
                variant="link"
                className="justify-self-start px-0"
                disabled={isSubmitting}
                onClick={() => switchMode('recovery')}
              >
                {t('mfa.useRecoveryCodeInstead')}
              </Button>
            </>
          )}
        </form>
      )}
    </AuthShell>
  );
}
